/**
 * Plagues API service
 * Handles all plague-related API operations against the DRF backend
 */

import { httpClient } from '@/utils';
import type {
    APIPlague,
    APIPlagueRequest,
    DRFPaginatedResponse,
    PlagueCategory,
} from '@/types/api-schemas';

const BASE = '/plagues/';

export interface PlagueListParams {
    page?: number;
    page_size?: number;
    active?: boolean;
    category?: PlagueCategory;
    name?: string;
    search?: string;
    ordering?: string;
}

/**
 * Build a query string from an arbitrary params object.
 * Boolean values are converted to 'true'/'false' strings.
 * undefined / null values are omitted.
 */
const buildQuery = (params: Record<string, unknown>): string => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
            usp.append(key, String(val));
        }
    });
    const qs = usp.toString();
    return qs ? `?${qs}` : '';
};

export const plaguesService = {
    /**
     * GET /plagues/  — paginated list with optional filters
     */
    list(params?: PlagueListParams): Promise<DRFPaginatedResponse<APIPlague>> {
        const query = params ? buildQuery(params as Record<string, unknown>) : '';
        return httpClient.get<DRFPaginatedResponse<APIPlague>>(`${BASE}${query}`);
    },

    /**
     * GET /plagues/:id/
     */
    getById(id: number): Promise<APIPlague> {
        return httpClient.get<APIPlague>(`${BASE}${id}/`);
    },

    /**
     * POST /plagues/
     */
    create(data: APIPlagueRequest): Promise<APIPlague> {
        return httpClient.post<APIPlague, APIPlagueRequest>(BASE, data);
    },

    /**
     * PUT /plagues/:id/
     */
    update(id: number, data: APIPlagueRequest): Promise<APIPlague> {
        return httpClient.put<APIPlague, APIPlagueRequest>(`${BASE}${id}/`, data);
    },

    /**
     * PATCH /plagues/:id/
     */
    patch(id: number, data: Partial<APIPlagueRequest>): Promise<APIPlague> {
        return httpClient.patch<APIPlague, Partial<APIPlagueRequest>>(`${BASE}${id}/`, data);
    },

    /**
     * DELETE /plagues/:id/
     */
    remove(id: number): Promise<void> {
        return httpClient.delete<void>(`${BASE}${id}/`);
    },
};

export default plaguesService;
